import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { Icon } from '@phosphor-icons/react';
import {
  CheckCircle,
  CheckSquare,
  Clock,
  Crown,
  GlobeHemisphereWest,
  PlayCircle,
  ProhibitInset,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';

import { cn } from '@/lib/utils';

import type { Appointment, AppointmentStatus, WeeklyCalendarLabels } from './types';

const STATUS_ICON: Record<AppointmentStatus, Icon> = {
  confirmed: CheckCircle,
  pending: Clock,
  cancelled: XCircle,
  no_show: ProhibitInset,
  in_progress: PlayCircle,
  completed: CheckSquare,
};

const STATUS_STYLES: Record<AppointmentStatus, string> = {
  confirmed: 'border-l-biz-success bg-biz-success-subtle text-biz-foreground',
  pending:
    'border-l-biz-warning bg-biz-warning-subtle text-biz-foreground border border-dashed border-biz-warning-border',
  cancelled: 'border-l-biz-danger bg-biz-danger-subtle text-biz-foreground',
  no_show: 'border-l-biz-border-strong bg-biz-muted text-biz-foreground-muted',
  in_progress:
    'border-l-biz-info bg-biz-info-subtle text-biz-foreground animate-biz-pulse-info',
  completed: 'border-l-biz-primary bg-biz-primary-subtle text-biz-foreground',
};

const STATUS_ICON_COLOR: Record<AppointmentStatus, string> = {
  confirmed: 'text-biz-success',
  pending: 'text-biz-warning',
  cancelled: 'text-biz-danger',
  no_show: 'text-biz-foreground-subtle',
  in_progress: 'text-biz-info',
  completed: 'text-biz-primary',
};

interface AppointmentCardProps {
  appointment: Appointment;
  labels: WeeklyCalendarLabels;
  topPx: number;
  heightPx: number;
  isConflicting?: boolean;
  isDimmed?: boolean;
  showStaffHint?: boolean;
  staffName?: string;
  onSelect?: (a: Appointment) => void;
}

export function AppointmentCard({
  appointment,
  labels,
  topPx,
  heightPx,
  isConflicting,
  isDimmed,
  showStaffHint,
  staffName,
  onSelect,
}: AppointmentCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: appointment.id,
    data: { appointment },
  });
  const StatusIcon = STATUS_ICON[appointment.status];
  const isCancelled = appointment.status === 'cancelled';

  const start = `${appointment.startTime.getHours().toString().padStart(2, '0')}:${appointment.startTime
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
  const end = `${appointment.endTime.getHours().toString().padStart(2, '0')}:${appointment.endTime
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;

  const isMarketplace = appointment.source === 'marketplace';
  const aria = `${labels.statusLabels[appointment.status]} ${labels.serviceLabel.toLowerCase()}: ${appointment.serviceName}, ${labels.petLabel.toLowerCase()} ${appointment.petName}, ${start} – ${end}${
    staffName ? `, ${staffName}` : ''
  }${appointment.isVip ? `, ${labels.vipBadge}` : ''}${isMarketplace ? `, ${labels.marketplaceBadge}` : ''}${
    isConflicting ? `, ${labels.conflictBadge}` : ''
  }`;

  return (
    <button
      ref={setNodeRef}
      type="button"
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(appointment);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(appointment);
        }
      }}
      aria-label={aria}
      data-appointment-id={appointment.id}
      style={{
        transform: CSS.Translate.toString(transform),
        top: topPx,
        height: heightPx,
        opacity: isDimmed ? 0.2 : isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : 5,
      }}
      className={cn(
        'absolute left-1 right-1 rounded-biz-card border-l-4 p-2 text-left',
        'transition-all duration-150 ease-biz-out biz-motion-safe',
        'shadow-biz-sm hover:shadow-biz-md hover:scale-[1.01] active:scale-[0.98]',
        'biz-focus-ring overflow-hidden',
        STATUS_STYLES[appointment.status],
        isDragging && 'shadow-biz-drag scale-[1.02] cursor-grabbing',
        !isDragging && 'cursor-grab',
        isConflicting && 'biz-conflict-stripes ring-2 ring-biz-danger/60',
      )}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex items-center gap-1 min-w-0">
          <StatusIcon
            size={14}
            weight="regular"
            className={cn('shrink-0', STATUS_ICON_COLOR[appointment.status])}
          />
          <span className="text-[10px] font-semibold uppercase tracking-wider truncate text-biz-foreground-muted">
            {labels.statusLabels[appointment.status]}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isConflicting && (
            <WarningCircle
              size={14}
              weight="regular"
              className="text-biz-danger"
              aria-label={labels.conflictBadge}
            />
          )}
          {appointment.isVip && (
            <span
              className="inline-flex items-center gap-0.5 rounded-full bg-biz-accent/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-biz-accent"
              aria-label={labels.vipBadge}
            >
              <Crown size={10} weight="regular" />
              {labels.vipBadge}
            </span>
          )}
          {isMarketplace && (
            <span
              className="inline-flex items-center gap-0.5 rounded-full bg-biz-info/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-biz-info"
              aria-label={labels.marketplaceBadge}
            >
              <GlobeHemisphereWest size={10} weight="regular" />
              {labels.marketplaceBadge}
            </span>
          )}
        </div>
      </div>

      <div className="mt-1 min-w-0">
        <div
          className={cn(
            'text-sm font-semibold truncate font-display',
            isCancelled && 'line-through opacity-70',
          )}
        >
          {appointment.petName}{' '}
          <span className="text-biz-foreground-muted font-normal">·</span>{' '}
          <span className="font-medium">{appointment.serviceName}</span>
        </div>
        <div className="font-mono-num text-xs text-biz-foreground-muted mt-0.5">
          {start} – {end}
        </div>
        {showStaffHint && staffName && heightPx > 60 && (
          <div className="text-[10px] text-biz-foreground-subtle truncate mt-0.5">
            {staffName}
          </div>
        )}
      </div>
    </button>
  );
}
